import { AppConstants } from 'cd-eft/appConstants';
import stores from 'cd-eft/models/Stores';
import { Renderers } from '../SETMap/SETMapRenderers';
import { appConfig } from 'cd-eft/config/AppConfig';
import {
  FeatureLayer,
  Geoprocessor,
  GraphicsLayer,
  SpatialReference,
  Point,
  Polyline,
  esriProjection,
  GroupLayer,
  MapView,
  SketchViewModel,
  SnappingOptions,
  Geometry,
  Graphic,
  Extent,
  esriGeometryEngine,
} from 'cd-eft/utils/esriTypes';
import { setCursorByID } from 'cd-eft/utils/setHtmlElementProperty';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { getSpatialRefByProject } from 'cd-eft/components/Projects/ProjectsLibrary/ProjectsLibraryVM'
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import { commonFields } from 'cd-eft/utils/gisUtils';
import { formatStationString } from 'cd-eft/utils/stationUtilities';

const libraryStore = stores.AppStore.projectsLibraryStore;
const setStore = stores.AppStore.surveyEngineeringStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;
const designStore = stores.AppStore.designStore;

export function showCoordinates(pt, details?: {d?: string, s?: string, pointName?: string}) {
  let locationInfo = '<div>X: ' + pt.x.toFixed(3) + '  Y: ' + pt.y.toFixed(3) + '</div>';
  if (pt.z) locationInfo += '<div>Elevation: ' + pt.z.toFixed(3) + '</div>';
  if (details) {
    if (details.d) locationInfo += '<div>Distance: ' + details.d + '</div>';
    if (details.s) locationInfo += '<div>Slope (%): ' + details.s + '</div>';
    if (details.pointName) locationInfo += '<div>Point Name: ' + details.pointName + '</div>';
  }

  if (gisModel && gisModel.locationWidget) {
    gisModel.locationWidget.innerHTML = locationInfo;
  }
}

export async function invokeExportZipHandler(
  input_design_json_ids: string,
  input_json_data: any,
  input_wkid: number,
  projectUuid: any,
  // input_json_data: '',
  // input_design_type: number // = -1
) {
  let messageIndex = 0;
  const options = {
    interval: 1000,
    signal: null,
    statusCallback: function (jobInfo) {
      // log any new job messages
      if (jobInfo.messages) {
        if (jobInfo.messages.length > messageIndex)
          for (let i = messageIndex; i < jobInfo.messages.length; i++) {
            // console.log(jobInfo.messages[i].description);
          }
        messageIndex = jobInfo.messages.length;
      }
    },
  };

  await getSpatialRefByProject(projectUuid);

  // console.log('exporting shapefiles files');
  // console.log('input_design_json_ids: ', input_design_json_ids);
  const gp = new Geoprocessor({
    url: appConfig.surfaceDesignGpApiConfig.basePath + appConfig.surfaceDesignGpApiConfig.EftExporterTool,
  });

  let params;
  if (libraryStore.projectSpatialReference?.wkid) {
    const wkid = libraryStore.projectSpatialReference.wkid;
    params = {
      input_design_json_ids: input_design_json_ids,
      input_json_data: input_json_data,
      input_design_type: -1,
      input_wkid: wkid,
    };
  } else if (libraryStore.projectSpatialReference?.wkt) {
    const wktJson = libraryStore.projectSpatialReference.wkt;
    params = {
      input_design_json_ids: input_design_json_ids,
      input_json_data: input_json_data,
      input_design_type: -1,
      input_wkt: wktJson,
    };
  }
  let jobInfo = await gp.submitJob(params);
  jobInfo = await gp.waitForJobCompletion(jobInfo.jobId, options);

  const zipUrl = await gp.getResultData(jobInfo.jobId, 'output_zipfile');
  // console.log('Download Url: ' + zipUrl.value.url);
  setStore.shapeFilesExportZipUrl = zipUrl.value.url;
}

export async function displaySurveyPointsClientSide(
  view: MapView,
  dataTable: any,
  layerId: string,
  layerName: string,
  turnOn: boolean,
  renderer: any,
  wkid: number,
  isNewSurvey = false,
  previewSR?: SpatialReference | undefined
) {
  const map = view.map;
  if (map) {
    // turn on/off the layer if already exists
    // let layerFound = false;
    // for (const lyr of map.layers) {
    //   if (lyr.id === layerId) {
    //     lyr.visible = turnOn;
    //     layerFound = true;
    //     break;
    //   }
    // }

    // remove the layer with the same layer id
    const existingLayerWithSameId = map.findLayerById(layerId);
    if (existingLayerWithSameId) {
      map.remove(existingLayerWithSameId);
    }

    /*** Add the new layer for the first time ***/
    const spatialReference = previewSR !== undefined ? previewSR : libraryStore.projectSpatialReference;

    if (turnOn) {
      // prepare the data source
      const source = dataTable.map((r, i) => {
        return {
          geometry: new Point({
            spatialReference: spatialReference,
            hasZ: true,
            x: r.x,
            y: r.y,
            z: r.z,
          }),
          attributes: {
            ObjectID: i,
            pointName: r.pointName,
            y: r.y,
            x: r.x,
            z: r.z,
            description: r.featureDescription,
          },
        };
      });

      const fields = commonFields;

      // create feature layer
      const pointlayer = new FeatureLayer({
        source,
        fields,
        renderer,
        id: layerId,
        title: layerName,
        objectIdField: 'ObjectID',
        spatialReference: spatialReference, // good to specify spatial reference at layer level
        geometryType: 'point', // good to specify what kind of shapes are in the layer
        hasZ: true,
      });

      // create the sketch layers for point pairing
      const importingNewSurvey = !setStore.currentSurvey.surveyId && setStore.currentSurvey.surveyName ? true : false;
      let transSketchLayer = map.findLayerById(AppConstants.transSketchLayerId);
      if (!transSketchLayer && importingNewSurvey) {
        transSketchLayer = new GraphicsLayer();
        transSketchLayer.id = AppConstants.transSketchLayerId;
        transSketchLayer.title = 'translation sketch';
        map.add(transSketchLayer);
      }
      let rotateSketchLayer = map.findLayerById(AppConstants.rotateSketchLayerId);
      if (!rotateSketchLayer && importingNewSurvey) {
        rotateSketchLayer = new GraphicsLayer();
        rotateSketchLayer.id = AppConstants.rotateSketchLayerId;
        rotateSketchLayer.title = 'rotate sketch';
        map.add(rotateSketchLayer);
      }
      const transIndex = map.layers.findIndex((lyr) => lyr.id === AppConstants.transSketchLayerId);
      const rotateIndex = map.layers.findIndex((lyr) => lyr.id === AppConstants.rotateSketchLayerId);

      let minIndex = map.layers.length;
      if (transIndex) {
        minIndex = transIndex;
      }
      if (rotateIndex && rotateIndex < minIndex) {
        minIndex = rotateIndex;
      }

      map.add(pointlayer, minIndex - 1);

      // set up translation sketchmodels
      view.whenLayerView(pointlayer).then((layerView) => {
        setUpTranslateSketchViewModels(view, pointlayer, transSketchLayer, rotateSketchLayer, isNewSurvey);
      });

      //*** zoom to the new extent
      if (pointlayer.source && pointlayer.source.length > 0 /* && setStore.surveySummaryTable.length === 0 */) {
        //have to wait for new layer to finish initializing to get "fullExtent" property
        await pointlayer.when();
        //have to project extent to view's coordinate system
        const zoomExtent = esriProjection.project(pointlayer.fullExtent, view.spatialReference) as Geometry;
        // instruct view to go to the desired zoom extent
        view.goTo(zoomExtent);
      }
    }
  }

  function setUpTranslateSketchViewModels(
    view: MapView,
    surveyPointLayer: FeatureLayer,
    transSketchLayer,
    rotateSketchLayer,
    isNewSurvey
  ) {
    // set up the SnappingOptions
    if (!setStore.refSnappingOptions) {
      setStore.refSnappingOptions = new SnappingOptions({
        distance: 21,
        enabled: true,
        featureSources: [],
      });
    }
    if (!setStore.targetSnappingOptions) {
      setStore.targetSnappingOptions = new SnappingOptions({
        distance: 21,
        enabled: true,
        featureSources: [],
      });
    }
    const featureSources = isNewSurvey
      ? setStore.refSnappingOptions.featureSources
      : setStore.targetSnappingOptions.featureSources;
    const existinglayerSource = featureSources.find((fs) => fs.layer.id === surveyPointLayer.id);
    if (existinglayerSource) {
      featureSources.remove(existinglayerSource);
      existinglayerSource.layer.destroy();
    }
    featureSources.add({ layer: surveyPointLayer } as any);

    // get the sketch view models
    if (setStore.transSketchVM) {
      setStore.transSketchVM.layer = transSketchLayer;
    } else {
      setStore.transSketchVM = new SketchViewModel({
        layer: transSketchLayer,
        view,
        updateOnGraphicClick: false,
        polylineSymbol: {
          type: 'simple-line',
          color: 'purple',
          style: 'long-dash',
          width: 1,
          marker: {
            color: 'purple',
            placement: 'end',
            style: 'arrow',
          },
        },
      });
    }
    if (setStore.rotateSketchVM) {
      setStore.rotateSketchVM.layer = rotateSketchLayer;
    } else {
      setStore.rotateSketchVM = new SketchViewModel({
        layer: rotateSketchLayer,
        view,
        updateOnGraphicClick: false,
        polylineSymbol: {
          type: 'simple-line',
          color: 'red',
          style: 'long-dash',
          width: 1,
          marker: {
            color: 'red',
            placement: 'end',
            style: 'arrow',
          },
        },
      });
    }

    for (const vm of [setStore.transSketchVM, setStore.rotateSketchVM]) {
      vm.on('create', (event) => {
        if ((event.state === 'active' || event.state === 'start') && event.toolEventInfo.type === 'vertex-add') {
          if (event.state === 'start') {
            vm.snappingOptions = setStore.targetSnappingOptions!;
          }

          extractTranslationValuesFromMap(
            view,
            event.toolEventInfo.vertices[0].coordinates,
            [transSketchLayer, rotateSketchLayer],
            vm,
            event
          );
        }
      });
    }
  }
}

function extractTranslationValuesFromMap(
  view: MapView,
  mapCoordinates: any,
  excludeList: any,
  vm: SketchViewModel,
  event: any
) {
  const screenPoint = view.toScreen(
    new Point({ x: mapCoordinates[0], y: mapCoordinates[1], spatialReference: view.spatialReference })
  );
  if (setStore[`${setStore.selectPointType}InSelectMode`]) {
    const pointTypes = ['pivotRef', 'pivotTarget', 'angleRef', 'angleTarget', 'transRef', 'transTarget'];
    for (const pntType of pointTypes) {
      setStore[`${pntType}InSelectMode`] = false;
    }

    if (setStore.selectPointType.endsWith('Target')) {
      const cursorStyle = setStore[`${setStore.selectPointType}InSelectMode`] ? 'crosshair' : 'default';
      setCursorByID('mainView', cursorStyle);
    }
    view.hitTest(screenPoint, excludeList).then((response) => {
      for (const result of response.results) {
        if (result) {
          const graphic = result.graphic;
          if (graphic && graphic.attributes && graphic.attributes.ObjectID !== undefined) {
            if (
              (graphic.layer.id !== AppConstants.newSurveyLayerId && setStore.selectPointType.endsWith('Ref')) ||
              (graphic.layer.id === AppConstants.newSurveyLayerId && setStore.selectPointType.endsWith('Target'))
            ) {
              vm.undo();
              vm.complete();
              if (graphic.layer.id === AppConstants.newSurveyLayerId) {
                MessageCenter.addWarning('You should select the target point in the existing survey (blue dots).');
              } else {
                MessageCenter.addWarning('You should select the reference point in the new survey (green triangles).');
              }
              setTimeout(() => {
                MessageCenter.clearMessages();
              }, 3000);

              return;
            }

            if (event.state === 'active') {
              vm.complete();
            }

            const oid = graphic.attributes.ObjectID;
            const found = (graphic.layer as FeatureLayer).source.find((i) => i.attributes.ObjectID === oid);
            if (found) {
              setStore[`${setStore.selectPointType}Name`] = found.attributes.pointName;
              setStore[`${setStore.selectPointType}X`] = found.attributes.x;
              setStore[`${setStore.selectPointType}Y`] = found.attributes.y;
              if (setStore.selectPointType.startsWith('pivot') || setStore.selectPointType.startsWith('trans')) {
                setStore[`${setStore.selectPointType}Z`] = found.attributes.z;
              }

              if (setStore.selectPointType.endsWith('Ref')) {
                setStore.selectPointType = setStore.selectPointType.replace('Ref', 'Target');
                setStore[`${setStore.selectPointType}InSelectMode`] = true;
              }

              if (event.state === 'active') {
                const markerSymbol = new SimpleMarkerSymbol({
                  style: 'circle',
                  outline: {
                    color: 'red',
                    width: 1,
                  },
                });

                if (event.toolEventInfo.type === 'vertex-add') {
                  const highlightGraphic = new Graphic({
                    geometry: new Point({
                      spatialReference: view.spatialReference,
                      x: event.toolEventInfo.vertices[0].coordinates[0],
                      y: event.toolEventInfo.vertices[0].coordinates[1],
                    }),
                    symbol: markerSymbol,
                  });
                  vm.layer.add(highlightGraphic);
                }
              }
              return;
            }
          }
        }
      }
    });
  }
}

/**
 * Display alignment design on the map as client-side layers.
 *
 * @param map
 * @param jsonData input source data in JSON format.
 * @param layerName alignment name or survey name
 * @param layerId unique id for the alignment layer.
 */
export async function DisplayAlignment(
  map: any,
  jsonData: any,
  layerName: string,
  layerId: string = 'Alignment',
  layerColor: string = 'blue',
  wkid: number,
  fromDeletion: boolean = false
) {
  if (!setStore.SETMapLayerKeepList.includes(layerId)) setStore.SETMapLayerKeepList.push(layerId);
  const pointsArray = jsonData.projectPoints;
  const alignment = jsonData.alignmentXY;
  const curvedAlignment = jsonData.curvedAlignmentXY;
  const surveyLines = jsonData.offsetStationLine;
  const alignmentStations = jsonData.alignmentStations;

  // const map = gisModel.map;
  if (map) {
    const groupLayer = new GroupLayer();

    // remove existing alignment layer
    let layerToRemove;
    for (const lyr of map.layers) {
      if (lyr.id === layerId) {
        // lyr.visible = turnOn;
        layerToRemove = lyr;
        break;
      }
    }
    if (layerToRemove) map.remove(layerToRemove);

    /*** Add alignment points layer ***/
    // prepare the point data source
    let alignmentPointlayer;
    if (pointsArray) {
      const source = pointsArray.map((r, i) => {
        return {
          geometry: new Point({
            spatialReference: libraryStore.projectSpatialReference,
            hasZ: true,
            x: r.x,
            y: r.y,
            z: r.z,
          }),
          attributes: {
            ObjectID: i,
            pointName: r.pointName,
            y: r.y,
            x: r.x,
            z: r.z,
            description: r.description,
          },
        };
      });

      const fields = commonFields;

      // create the points layer
      alignmentPointlayer = new FeatureLayer({
        source,
        fields,
        renderer: Renderers.alignmentPointRenderer as any,
        id: 'alignment_points',
        title: 'Survey Points',
        objectIdField: 'ObjectID',
      });
    }

      /*** Add Alignment Label Layer ***/
      const pointSource = [] as Graphic[];
    
    if(alignmentStations && alignmentStations.length > 0){
      const length = alignmentStations.length;
      for (let i = 0; i < length; i++) {
        const stationName = alignmentStations[i].stationName;
        const stationLabel = stationName ? formatStationString(stationName.toString()) : '';      
        const point = new Graphic({
          geometry: new Point({
            spatialReference:libraryStore.projectSpatialReference,
            x: alignmentStations[i].x,
            y: alignmentStations[i].y,
          }),
          attributes: {
            ObjectID: i,
            Station: stationLabel
          },
        });
        pointSource.push(point);
      }
    }

    const pointLabelFields: any = [
      {
        name: 'ObjectID',
        type: 'oid',
      },
      {
        name: 'Station',
        type: 'string',
      }
    ];
    const pointLabelClass: LabelClass = new LabelClass({
      symbol: {
        type: 'text',
        color: "black",
        font: {
          family: 'Ubuntu Mono',
          size: 11,
        },
      },
      labelPlacement: "below-left",
      labelPosition: 'curved',
      labelExpressionInfo: {
        expression: '$feature.station',
      },
      deconflictionStrategy: 'none',
    });
    pointLabelClass.minScale = 2000;
    // pointLabelClass.minScale = gisModel.view!.scale * 0.9;
    const alingmenStationsLabelLayer = new FeatureLayer({
      source: pointSource,
      fields: pointLabelFields,
      renderer: Renderers.alignmentStationLabelRenderer as any,
      id: 'AlignmentStationLabels',
      title: 'Alignment Station Labels',
      objectIdField: 'ObjectID',
      displayField: 'Station',
      labelingInfo: [pointLabelClass]
    });

    /*** Add survey offset lines ***/
    let offsetLinelayer;
    if (surveyLines) {
      const sourceOffsetLine = surveyLines.map((r, i) => {
        const startPointZ = pointsArray.find((p) => p.id === r.offsetStationLineStartPointId);
        const endPointZ = pointsArray.find((p) => p.id === r.offsetStationLineEndPointId);

        let paths;
        if (startPointZ && endPointZ) {
          paths = [
            [
              [startPointZ.x, startPointZ.y, startPointZ.z],
              [endPointZ.x, endPointZ.y, endPointZ.z],
            ],
          ];
        } else {
          paths = [[]];
        }

        return {
          geometry: new Polyline({
            spatialReference: libraryStore.projectSpatialReference,
            hasZ: true,
            paths,
          }),
          attributes: {
            ObjectID: i,
          },
        };
      });

      // create feature layer
      offsetLinelayer = new FeatureLayer({
        source: sourceOffsetLine,
        fields: [
          {
            name: 'ObjectID',
            alias: 'ObjectID',
            type: 'oid',
          },
        ],
        renderer: Renderers.alignmentStationOffsetLineRenderer as any,
        id: 'offset_lines',
        title: 'Offset Lines',
        objectIdField: 'ObjectID',
      });
    }

    /*** create alignment centerline ***/
    // prepare the data source
    let alignmentCenterlineLayer;
    if (alignment && !fromDeletion) {
      const paths = [
        alignment.map((r) => {
          return [r.x, r.y];
        }),
      ];

      const sourceAlignment = [
        {
          geometry: new Polyline({
            spatialReference: libraryStore.projectSpatialReference,
            paths,
          }),
          attributes: {
            ObjectID: 0,
          },
        },
      ];

      // create feature layer
      alignmentCenterlineLayer = new FeatureLayer({
        source: sourceAlignment,
        fields: [
          {
            name: 'ObjectID',
            alias: 'ObjectID',
            type: 'oid',
          },
        ],
        renderer: Renderers.alignmentCenterlineRenderer as any,
        id: 'alignment_centerline',
        title: 'Alignment Centerline',
        objectIdField: 'ObjectID',
      });
      if (layerColor && alignmentCenterlineLayer.renderer) {
        alignmentCenterlineLayer.renderer.symbol.color = layerColor;
      }

      // zoom to the alignment
      if (sourceAlignment[0]) {
        await esriProjection.load();
        const extent = esriProjection.project(
          sourceAlignment[0].geometry.extent.expand(1.2),
          new SpatialReference({
            wkid: 4326, //upprojected to lantitude and longitude
          })
        ) as Extent;

        if (gisModel.view && gisModel.view.extent && gisModel.view.extent.center && extent && extent.center) {
          const currentCenter = gisModel.view.extent.center;
          const newCenter = esriProjection.project(extent.center, gisModel.view.spatialReference) as Point;
          const distance = esriGeometryEngine.distance(currentCenter, newCenter);
          if (5000 < Math.min(gisModel.view.extent.height, gisModel.view.extent.width) || 5000 < distance) {
            zoomTo(extent);
          }
        }
      }
    }

    /*** create alignment curved line ***/
    // prepare the data source
    let curvedAlignmentLayer;
    if (curvedAlignment) {
      const paths = [
        curvedAlignment.map((r) => {
          return [r.x, r.y];
        }),
      ];

      const sourceAlignment = [
        {
          geometry: new Polyline({
            spatialReference: libraryStore.projectSpatialReference,
            paths,
          }),
          attributes: { ObjectID: 0 },
        },
      ];

      // create feature layer
      curvedAlignmentLayer = new FeatureLayer({
        source: sourceAlignment,
        fields: [
          {
            name: 'ObjectID',
            alias: 'ObjectID',
            type: 'oid',
          },
        ],
        renderer: Renderers.alignmentCenterlineRenderer as any,
        id: 'curved_alignment',
        title: 'Curved Alignment',
        objectIdField: 'ObjectID',
      });

      if (curvedAlignmentLayer.renderer) curvedAlignmentLayer.renderer.symbol.color = 'green';
    }

    // show alignment on the map
    // There is a logical problem here: some of these may be undefined,
    // but it still attempts to add them as layers to the map.
    // However, this is not a breaking error, just an error in the console
    if (offsetLinelayer || alignmentCenterlineLayer || alignmentPointlayer || curvedAlignmentLayer) {
      if (offsetLinelayer) groupLayer.add(offsetLinelayer);
      if (alignmentCenterlineLayer) groupLayer.add(alignmentCenterlineLayer);
      if (curvedAlignmentLayer) groupLayer.add(curvedAlignmentLayer);
      if (alignmentPointlayer) groupLayer.add(alignmentPointlayer);
      if(alingmenStationsLabelLayer)groupLayer.add(alingmenStationsLabelLayer);
      groupLayer.id = layerId;
      groupLayer.title = layerName; // 'Alignment';
      // const layerIndex = map.layers.length - 4 < 0 ? map.layers.length - 1 : map.layers.length - 4;
      map.add(groupLayer);
    }
  }
}

function zoomTo(extent: __esri.Extent) {
  gisModel.view.goTo(extent);
  designStore.setHomeWidgetTargetExtent(extent);
}
